import { LSKeys, LocalStorage } from '@core/localStorage';
import { ThemeMode, ThemeModeConstants } from '@helpers/theme.helper';
import { FSDemmi } from '@models/demmi.model';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { RootState } from '../store';

interface AppState {
  themeMode: ThemeMode;
  firestoreDemmi?: FSDemmi;
  activeRequests: string[];
  globalContentMounted: boolean;
  isDevMode: boolean;
}

const initialState: AppState = {
  themeMode: LocalStorage.get(LSKeys.THEME_MODE) as ThemeMode,
  firestoreDemmi: undefined,
  activeRequests: [],

  globalContentMounted: false,
  isDevMode: true,
};

export const appSlice = createSlice({
  name: 'app',
  initialState,
  extraReducers: builder => {
    builder.addCase('LOGOUT', state => {
      Object.assign(state, initialState);
    });
  },
  reducers: {
    setThemeMode: (state, action: PayloadAction<ThemeModeConstants>) => {
      state.themeMode = action.payload;
    },

    pushNetworkRequestUUID: (state, { payload: uuid }: PayloadAction<string>) => {
      state.activeRequests = [...state.activeRequests, uuid];
    },

    popNetworkRequestUUID: (state, { payload: uuid }: PayloadAction<string>) => {
      state.activeRequests = state.activeRequests.filter(request => request !== uuid);
    },

    setFSDemmi: (state, { payload }: PayloadAction<FSDemmi | undefined>) => {
      state.firestoreDemmi = payload;
    },

    setGlobalContentMounted: (state, { payload }: PayloadAction<boolean>) => {
      if (state.globalContentMounted !== payload) state.globalContentMounted = payload;
    },

    setIsDevMode: (state, { payload }: PayloadAction<boolean>) => {
      if (state.isDevMode !== payload) state.isDevMode = payload;
    },
  },
});

export const {
  setThemeMode,
  pushNetworkRequestUUID,
  popNetworkRequestUUID,
  setFSDemmi,
  setGlobalContentMounted,
  setIsDevMode,
} = appSlice.actions;

export const selectThemeMode = (state: RootState) => state.app.themeMode;
export const selectFSDemmi = (state: RootState) => state.app.firestoreDemmi;
export const selectIsLoading = (state: RootState) => state.app.activeRequests.length > 0;
export const selectActiveRequests = (state: RootState) => state.app.activeRequests;
export const selectGlobalContentMounted = (state: RootState) => state.app.globalContentMounted;
export const selectIsDevMode = (state: RootState) => state.app.isDevMode;

export default appSlice.reducer;
